// ─── Open-redirect guard for post-login returnUrl ──────────────────────────
// A `returnUrl` carried through the login flow is attacker-controllable (it's a
// query param). Redirecting to it unsanitized is an open redirect — a phishing
// vector, and this app holds student data + payments. So we ALLOWLIST: a return
// destination must be a root-relative internal path whose first segment is a
// known app section. Everything else (absolute URLs, protocol-relative `//host`,
// schemes, auth/error paths, junk) is rejected → caller falls back to the
// role-based default route.

/**
 * Valid first path segments for a post-login destination. Derived from the app's
 * top-level routes; intentionally EXCLUDES auth/error/public-funnel paths
 * (`login`, `auth`, `no-access`, `apply`, `registro`) so a returnUrl can never
 * bounce back into the login flow or out of the authed surface.
 */
const ALLOWED_SEGMENTS = new Set([
  'academic',
  'admin',
  'admissions',
  'anuncios',
  'assignments',
  'catalogo-libros',
  'crm',
  'curso',
  'data-audit',
  'empezar',
  'form-templates',
  'grades',
  'materiales',
  'people',
  'plantillas-syllabus',
  'plataforma',
  'portal',
  'portal-v2',
  'reportes',
  's',
  'scholarships',
  'sections',
  'students',
  'settings',
  'tuition',
  'usuarios',
]);

/** True if the string contains any ASCII control char (CR/LF/NUL header-injection). */
function hasControlChar(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    if (s.charCodeAt(i) < 0x20) return true;
  }
  return false;
}

/**
 * Returns a safe internal path+query to redirect to, or null if `raw` is not an
 * allowlisted internal destination. Never returns an absolute/cross-origin URL.
 */
export function sanitizeReturnUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return null; // malformed percent-encoding
  }

  // Must be root-relative. Reject protocol-relative (`//evil`, `/\evil`) and
  // anything with control chars.
  if (!decoded.startsWith('/')) return null;
  if (decoded.startsWith('//') || decoded.startsWith('/\\')) return null;
  if (hasControlChar(decoded)) return null;

  // Resolve against a sentinel origin; if it escapes the origin it's not a safe
  // relative path (catches `https://`, `javascript:`, backslash tricks, etc.).
  let url: URL;
  try {
    url = new URL(decoded, 'https://internal.invalid');
  } catch {
    return null;
  }
  if (url.origin !== 'https://internal.invalid') return null;

  const segments = url.pathname.split('/').filter(Boolean);
  const firstSegment = segments[0] ?? '';
  if (!ALLOWED_SEGMENTS.has(firstSegment)) return null;

  // Defense in depth: never return to an auth path at ANY depth (e.g.
  // `/s/{slug}/login`), which would re-enter the login loop.
  if (segments.some((s) => s === 'login' || s === 'logout' || s === 'auth')) return null;

  // Path + query only; drop any fragment.
  return url.pathname + url.search;
}
